<template>
  <div>
    <div class="headline5 mb-4">
      {{ $t(`modals.addGlobalMspPolicy.policies.userDataGovernance.title`) }}
    </div>
    <v-tabs
      v-model="selectedTab"
      align-with-title
      color="orange-base"
      background-color="white"
      slider-color="orange-base"
      :height="48"
    >
      <v-tab v-for="item in tabs" :key="item" class="mr-8" :height="42">
        {{ item }}
      </v-tab>
    </v-tabs>
    <v-window v-model="selectedTab">
      <v-window-item v-for="item in tabs" :key="item">
        <div v-if="selectedTab === 0" class="mt-4">
          <dlp-settings-content
            data-testid="dlp-settings-content"
            :action-not-allowed="disabled"
            :other-data-types="otherDataTypes"
            :recommended-data-types="recommendedDataTypes"
            :model-value="localValue.monitoring"
            @set-parameters="onSetParametersClick"
          />
          <user-data-settings-content
            v-model="localValue.monitoring"
            :action-not-allowed="disabled"
          />
        </div>
        <template v-else>
          <v-card class="mt-3" width="100%" data-testid="dlp-exclusions-tab">
            <v-card-text class="px-8 py-10">
              <div class="body1 text--semibold mb-3">
                {{ $t("userDataGovernanceSettings.exclusionsTab.title") }}
              </div>
              <email-subject-keywords-input v-model="localValue.exclusions" :disabled="disabled" />
            </v-card-text>
          </v-card>
        </template>
      </v-window-item>
    </v-window>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, watch, type PropType, onMounted, type Ref, computed } from "vue";
import { defaultEmptyPolicies, GlobalPolicySection } from "@/constants/msp.ts";
import cloneDeep from "lodash/cloneDeep";
import type { GlobalPolicyConfig } from "@/_store/msp/global-policies/msp-global-policies.module.ts";
import DlpSettingsContent from "@/components/settings/DlpSettingsContent.vue";
import { axiosInstance } from "@/plugins/https.ts";
import api from "@/_helpers/api.ts";
import { getRecommendedAndOtherDataTypes } from "@/_store/user-data-governance/adapter.ts";
import { EndpointDataGovernanceAction } from "@/constants/endpoint-data-governance.ts";
import SetIndustryModal from "@/components/modals/data-governance/SetIndustryModal.vue";
import { ModalWidth } from "@/constants/modals.ts";
import { useDialogsStore } from "@/_store/dialogs.module.ts";
import UserDataSettingsContent from "@/components/settings/UserDataSettingsContent.vue";
import { useI18n } from "vue-i18n";
import EmailSubjectKeywordsInput from "@/components/EmailSubjectKeywordsInput.vue";
import type { UserDataGovernanceSettings } from "@/_store/user-data-governance/user-data-governance.module.ts";
import { componentDialogsConfigConstructor } from "@/_helpers/dialog-helpers.ts";

export default defineComponent({
  components: {
    EmailSubjectKeywordsInput,
    UserDataSettingsContent,
    DlpSettingsContent,
  },
  props: {
    data: {
      type: Object as PropType<GlobalPolicyConfig>,
      required: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["update:data"],
  setup(props, { emit }) {
    const i18n = useI18n();
    const dialogsStore = useDialogsStore();
    const localValue = ref(
      cloneDeep(
        props.data[GlobalPolicySection.USER_DATA_GOVERNANCE] ??
          defaultEmptyPolicies[GlobalPolicySection.USER_DATA_GOVERNANCE]
      )
    );
    const selectedTab = ref(0);
    const recommendedDataTypes: Ref<string[]> = ref([]);
    const otherDataTypes: Ref<string[]> = ref([]);
    const tabs = computed(() => [
      i18n.t("userDataGovernanceSettings.tabs.monitoring"),
      i18n.t("userDataGovernanceSettings.tabs.exclusions"),
    ]);
    async function getRecommendedAndOtherDataTypesData(industries: string[]): Promise<{
      recommendedDataTypes: string[];
      otherDataTypes: string[];
      settings: Omit<UserDataGovernanceSettings, "customData">;
    }> {
      try {
        const { data } = await axiosInstance.request({
          ...api.privacyParametersConfig(),
          params: {
            industries: industries.join(","),
          },
        });
        const dataTypes = getRecommendedAndOtherDataTypes(data);
        return {
          recommendedDataTypes: dataTypes.recommendedDataTypes,
          otherDataTypes: dataTypes.otherDataTypes,
          settings: data,
        };
      } catch (error) {
        console.error(error);
        return {
          recommendedDataTypes: [],
          otherDataTypes: [],
          settings: {} as Omit<UserDataGovernanceSettings, "customData">,
        };
      }
    }

    onMounted(async () => {
      const result = await getRecommendedAndOtherDataTypesData(
        localValue.value.monitoring.wizardParameters.industries
      );
      recommendedDataTypes.value = result.recommendedDataTypes;
      otherDataTypes.value = result.otherDataTypes;
    });

    const onSetParametersClick = () => {
      const dialogConfig = {
        ...componentDialogsConfigConstructor({
          action: EndpointDataGovernanceAction.SET_INDUSTRY,
          component: SetIndustryModal,
          width: ModalWidth.LARGE,
          disable: false,
          item: localValue.value?.monitoring.wizardParameters.isDefault
            ? { selectedIndustries: [] }
            : localValue.value?.monitoring.wizardParameters,
          callback: async (val: { selectedIndustries: string[] }) => {
            const result = await getRecommendedAndOtherDataTypesData(val.selectedIndustries);
            recommendedDataTypes.value = result.recommendedDataTypes;
            otherDataTypes.value = result.otherDataTypes;
            localValue.value.monitoring = {
              customData: localValue.value.monitoring.customData,
              ...result.settings,
            };
          },
        }),
      };

      dialogsStore.openDialog(dialogConfig);
    };

    watch(
      localValue,
      (newVal) => {
        emit("update:data", {
          ...props.data,
          [GlobalPolicySection.USER_DATA_GOVERNANCE]: newVal,
        });
      },
      { immediate: true, deep: true }
    );

    return {
      localValue,
      recommendedDataTypes,
      onSetParametersClick,
      otherDataTypes,
      selectedTab,
      tabs,
    };
  },
});
</script>

<style scoped lang="scss"></style>
